import { Checkbox, Grid, Popover, Typography } from 'gu-ui-kit'
import React from 'react'
import EditableField from 'components/EditableField'
import styles from 'modules/Services/CampaignsEditor/campaigns-editor.module.scss'
import HeaderWithTooltip from 'modules/Services/CampaignsEditor/subcomponents/HeaderWithTooltip'
import { StatusBadge } from 'modules/Services/CampaignsEditor/subcomponents/StatusBadge'
import InfoPopover from 'modules/Services/CampaignsEditor/subcomponents/InfoPopover'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

export default (
  { applyName, setColumnVisibility, setRowSelection, setSorting, ...rest },
  t,
) => {
  const { sm } = useBreakpoint()

  return {
    columns: [
      {
        id: 'checkbox',
        label: t('campaigns_editor::banners.table.head.checkbox.label'),
        enableHiding: false,
        header: ({ table }) => (
          <Grid justifyContent="center">
            <Checkbox
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          </Grid>
        ),
        cell: ({ row }) => (
          <Grid justifyContent="center">
            <Checkbox
              checked={row.getIsSelected()}
              onChangeHandle={row.getToggleSelectedHandler()}
            />
          </Grid>
        ),
      },
      {
        enableHiding: false,
        id: 'name',
        label: t('campaigns_editor::banners.table.head.name.label'),
        accessorKey: 'name',
        header: (
          <HeaderWithTooltip
            title={t('campaigns_editor::banners.table.head.name.label')}
            tooltipText={t('campaigns_editor::banners.table.head.name.tooltip')}
          />
        ),
        cell: ({ row }) => {
          const {
            original: { name, external_id, status },
          } = row

          const events = sm
            ? { triggerOpenEventName: 'onClick' }
            : {
                triggerOpenEventName: 'onMouseEnter',
                triggerCloseEventName: 'onMouseLeave',
              }

          //eslint-disable-next-line
          const { bindPopover, usePopupState, bindTrigger } =
            Popover.usePopover(events)

          //eslint-disable-next-line
          const popoverState = usePopupState({
            variant: 'popover',
            id: 'info-popover' + external_id,
          })

          return (
            <Grid direction={'column'} gap={8} alignItems="flex-start">
              <Grid
                className={styles['campaigns-editor__edit-name-container']}
                fluid={false}
                direction="column"
                alignItems="flex-start"
                justifyContent="start"
                gap={4}
                {...bindTrigger(popoverState)}
              >
                <Typography
                  variant={'Body-3'}
                  component={
                    <EditableField
                      disabled={status === 'deleted'}
                      editModeColor={'inherit'}
                      color={'Label-GU-Primary'}
                      id={external_id}
                      name={name}
                      login={name}
                      apply={applyName}
                    >
                      {name}
                    </EditableField>
                  }
                />
                <InfoPopover
                  short={true}
                  resource={'banners'}
                  popoverState={popoverState}
                  bindPopover={bindPopover}
                  data={row.original}
                />
              </Grid>
            </Grid>
          )
        },
      },
      {
        id: 'agency_client_name',
        label: t(
          'campaigns_editor::banners.table.head.agency_client_name.label',
        ),
        accessorKey: 'agency_client_name',
        header: (
          <HeaderWithTooltip
            title={t(
              'campaigns_editor::banners.table.head.agency_client_name.label',
            )}
            tooltipText={t(
              'campaigns_editor::banners.table.head.agency_client_name.tooltip',
            )}
          />
        ),
        cell: ({
          row: {
            original: { agency_client_name },
          },
        }) => agency_client_name ?? '—',
      },
      {
        id: 'external_id',
        label: t('campaigns_editor::banners.table.head.external_id.label'),
        accessorKey: 'external_id',
        header: (
          <HeaderWithTooltip
            title={t('campaigns_editor::banners.table.head.external_id.label')}
            tooltipText={t(
              'campaigns_editor::banners.table.head.external_id.tooltip',
            )}
          />
        ),
        cell: ({
          row: {
            original: { external_id },
          },
        }) => external_id ?? '—',
      },
      {
        id: 'status',
        label: t('campaigns_editor::banners.table.head.status.label'),
        accessorKey: 'status',
        header: t('campaigns_editor::banners.table.head.status.label'),
        cell: ({
          row: {
            original: { status },
          },
        }) => (
          <>
            <StatusBadge
              status={status}
              statusesMap={{
                active: {
                  icon: 'status active',
                  color: 'Label-Confirm',
                  bgColor: 'Background-Confirm',
                  label: 'campaigns_editor::banners.table.badges.active',
                },
                blocked: {
                  icon: 'status warning',
                  color: 'Label-Error-Primary',
                  bgColor: 'Background-Error',
                  label: 'campaigns_editor::banners.table.badges.blocked',
                },
                deleted: {
                  icon: 'inbox-archive',
                  color: 'Label-Fifth',
                  bgColor: 'Background-Quaternary',
                  label: 'campaigns_editor::banners.table.badges.archived',
                },
              }}
            />
          </>
        ),
      },
      {
        id: 'spent',
        label: t('campaigns_editor::banners.table.head.spent.label'),
        //accessorKey: 'spent',
        header: t('campaigns_editor::banners.table.head.spent.label'),
        cell: ({
          row: {
            original: { spent },
          },
        }) => spent ?? '—',
      },
      {
        id: 'impressions',
        label: t('campaigns_editor::banners.table.head.impressions.label'),
        //accessorKey: 'impressions',
        header: t('campaigns_editor::banners.table.head.impressions.label'),
        cell: ({
          row: {
            original: { impressions },
          },
        }) => impressions ?? '—',
      },
      {
        id: 'clicks',
        label: t('campaigns_editor::banners.table.head.clicks.label'),
        //accessorKey: 'clicks',
        header: t('campaigns_editor::banners.table.head.clicks.label'),
        cell: ({
          row: {
            original: { clicks },
          },
        }) => clicks ?? '—',
      },
      {
        id: 'goals',
        label: t('campaigns_editor::banners.table.head.goals.label'),
        //accessorKey: 'goals',
        header: (
          <HeaderWithTooltip
            title={t('campaigns_editor::banners.table.head.goals.label')}
            tooltipText={t(
              'campaigns_editor::banners.table.head.goals.tooltip',
            )}
          />
        ),
        cell: ({
          row: {
            original: { goals },
          },
        }) => goals ?? '—',
      },
      {
        id: 'cr',
        label: t('campaigns_editor::banners.table.head.cr.label'),
        //accessorKey: 'cr',
        header: t('campaigns_editor::banners.table.head.cr.label'),
        cell: ({
          row: {
            original: { cr },
          },
        }) => cr ?? '—',
      },
      {
        id: 'cpa',
        label: t('campaigns_editor::banners.table.head.cpa.label'),
        //accessorKey: 'cpa',
        header: t('campaigns_editor::banners.table.head.cpa.label'),
        cell: ({
          row: {
            original: { cpa },
          },
        }) => cpa ?? '—',
      },
      {
        id: 'ctr',
        label: t('campaigns_editor::banners.table.head.ctr.label'),
        //accessorKey: 'ctr',
        header: t('campaigns_editor::banners.table.head.ctr.label'),
        cell: ({
          row: {
            original: { ctr },
          },
        }) => ctr ?? '—',
      },
      {
        id: 'cpc',
        label: t('campaigns_editor::banners.table.head.cpc.label'),
        //accessorKey: 'cpc',
        header: t('campaigns_editor::banners.table.head.cpc.label'),
        cell: ({
          row: {
            original: { cpc },
          },
        }) => cpc ?? '—',
      },
      {
        id: 'cpm',
        label: t('campaigns_editor::banners.table.head.cpm.label'),
        //accessorKey: 'cpm',
        header: t('campaigns_editor::banners.table.head.cpm.label'),
        cell: ({
          row: {
            original: { cpm },
          },
        }) => cpm ?? '—',
      },
    ],
    showHeader: true,
    showFooter: false,
    className: styles['campaigns-editor__table'],
    //manualSorting: true,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.external_id,
    ...rest,
  }
}
